'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import React, { useRef } from 'react';

import {
  UiKitPaginationBase,
  UiKitPaginationBaseProps
} from '@/fo/ui/kit/pagination/base';
import { UiModHeadFixed, useStickyVisibility } from '@/fo/ui/mod/head/fixed';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { CareTagsList } from '../components/CareTagsList/CareTagsList';
import { FiltersPanel } from '../components/FiltersPanel/FiltersPanel';
import { SortBlock } from '../components/SortBlock/SortBlock';
import { DEFAULT_SORT_OPTION, SORT_OPTIONS } from '../constants';
import {
  FilterChangeArgs,
  FilterKeys,
  FilterOption,
  Filters,
  SelectedFilters
} from '../types';
import { handleMainFilterChange } from '../util/catalog-filters';

import { styles } from './UiProductLayoutCatalog.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

/** Компонент каталога продуктов с фильтрами, пагинацией и SEO-блоками */
export interface UiProductLayoutCatalogProps {
  /** Контент каталога (список продуктов) */
  children?: React.ReactNode;
  /** Кастомный блок над фильтрами */
  topCustomBlock?: React.ReactNode;
  /** Обработчик клика */
  onClick?: NativeElement['onClick'];
  /** Параметры пагинации */
  pagination: {
    count: UiKitPaginationBaseProps['count'];
    page: UiKitPaginationBaseProps['page'];
  };
  /** SEO-блок в верхней части каталога */
  topSeoBlock: React.ReactNode;
  /** SEO-блок в нижней части каталога */
  bottomSeoBlock?: React.ReactNode;
  /** Фильтры в верхней части каталога (теги)*/
  topFilters?: FilterOption[];
  /** Конфигурация фильтров */
  filters: Filters;
  /** Выбранные значения фильтров */
  selectedFilters: SelectedFilters;
  /** Выбранные значения сортировки */
  selectedSort: string;
  /** Обработчик изменения фильтров */
  onFiltersChange?: (args: FilterChangeArgs, withUpdate?: boolean) => void;
  /** Количество продуктов, которые будут отображены в каталоге */
  productsCount?: number;
  /** Обработчик отображения продуктов */
  onShowProducts?: () => void;
  /** Обработчик изменения открытия фильтров */
  onOpenChange?: (open: boolean) => void;
  /** Обработчик сброса фильтров */
  onReset?: (key: FilterKeys) => void;
  /** Обработчик изменения сортировки */
  onSortChange?: (option: FilterOption | null) => void;
  /** Обработчик изменения пагинации */
  setPagination?: (pagination: { count: number; page: number }) => void;
  /** Флаг загрузки */
  isLoading?: boolean;
  /** Обработчик загрузки большего количества продуктов */
  onLoadMore?: () => void;
  /** Флаг открытия модального окна */
  isOpenModal?: boolean;
  isCatalog?: boolean;
  hideCareOptions?: boolean;
  hideSort?: boolean;
  hideFilters?: boolean;
  isSearchPage?: boolean;
  gtmEventCategory?: string;
}

export function UiProductLayoutCatalog({
  children,
  topCustomBlock,
  topSeoBlock,
  pagination,
  bottomSeoBlock,
  topFilters = [],
  filters,
  selectedFilters,
  selectedSort = DEFAULT_SORT_OPTION.value,
  onFiltersChange = () => {},
  productsCount = 0,
  onShowProducts = () => {},
  onOpenChange = () => {},
  onReset = () => {},
  onSortChange = () => {},
  setPagination = () => {},
  isLoading = false,
  onLoadMore = () => {},
  isOpenModal = false,
  hideCareOptions = false,
  hideSort = false,
  hideFilters = false,
  isSearchPage = false,
  gtmEventCategory
}: UiProductLayoutCatalogProps) {
  const t = useTranslations();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const slots = styles();
  const { count = 0, page = 1 } = pagination;
  const refRoot = useRef<HTMLDivElement>(null);

  const {
    showSticky,
    rootRef: rootRefSticky,
    onInteraction
  } = useStickyVisibility();

  /** Список slug для PickUpCare фильтров */
  const pickUpCareSlugs = React.useMemo(
    () => filters.pickUpCareFilters.map((f) => f.slug),
    [filters.pickUpCareFilters]
  );

  const handleMainFilterChangeLocal = (
    slug: string,
    value: string | [number, number],
    checked: boolean,
    withUpdate?: boolean
  ) => {
    handleMainFilterChange(
      slug,
      value,
      checked,
      selectedFilters,
      onFiltersChange,
      withUpdate,
      pickUpCareSlugs
    );
  };

  const handleShowProducts = () => onShowProducts?.();

  const handlePaginationChange = (page: number) => {
    utilScrollToElement(refRoot.current);
    setPagination({ count, page });
  };

  const getPageHref = (targetPage: number) => {
    const params = new URLSearchParams(searchParams.toString());
    const n = targetPage >= 1 ? targetPage : 1;
    if (n <= 1) {
      params.delete('p');
    } else {
      params.set('p', String(n));
    }

    const query = params.toString();

    return query ? `${pathname}?${query}` : pathname;
  };

  return (
    <div ref={refRoot}>
      {!hideCareOptions && (
        <CareTagsList
          className={slots.careTagsList()}
          tagsList={topFilters}
          onFilterChange={handleMainFilterChangeLocal}
          pickUpCareFilters={filters.pickUpCareFilters}
          selectedFilters={selectedFilters}
          onReset={() => onReset?.(FilterKeys.pickUpCareFilters)}
          onShowProducts={handleShowProducts}
          productsCount={productsCount}
          onOpenChange={onOpenChange}
          isOpenModal={isOpenModal}
          isLoading={isLoading}
          gtmEventCategory={gtmEventCategory}
        />
      )}
      {Boolean(topSeoBlock) && (
        <div className={slots.gammaSeoBlock()}>{topSeoBlock}</div>
      )}
      {topCustomBlock}
      <div
        className={slots.filtersWrapper()}
        ref={rootRefSticky}
      >
        <div className={slots.filters({ notFixed: true })}>
          {!hideFilters && (
            <FiltersPanel
              filters={filters.mainFilters}
              selectedFilters={selectedFilters}
              onFilterChange={handleMainFilterChangeLocal}
              onReset={() => onReset?.(FilterKeys.mainFilters)}
              onOpenChange={onOpenChange}
              onShowProducts={handleShowProducts}
              productsCount={productsCount}
              isLoading={isLoading}
              isOpenModal={isOpenModal}
              gtmEventCategory={gtmEventCategory}
            />
          )}
          {!hideSort && (
            <SortBlock
              sortByFilters={SORT_OPTIONS(t)}
              selectedSortByFilters={selectedSort || DEFAULT_SORT_OPTION.value}
              onSortChange={onSortChange}
            />
          )}
        </div>
      </div>
      <UiModHeadFixed
        order={2}
        show={showSticky}
        positioning="fixed"
        onClick={onInteraction}
      >
        <div className={slots.filtersWrapper()}>
          <div className={slots.filters()}>
            {!hideFilters && (
              <FiltersPanel
                filters={filters.mainFilters}
                selectedFilters={selectedFilters}
                onFilterChange={handleMainFilterChangeLocal}
                onReset={() => onReset?.(FilterKeys.mainFilters)}
                onOpenChange={onOpenChange}
                onShowProducts={handleShowProducts}
                productsCount={productsCount}
                isLoading={isLoading}
                isOpenModal={isOpenModal}
              />
            )}
            {!hideSort && (
              <SortBlock
                sortByFilters={SORT_OPTIONS(t)}
                selectedSortByFilters={
                  selectedSort || DEFAULT_SORT_OPTION.value
                }
                onSortChange={onSortChange}
              />
            )}
          </div>
        </div>
      </UiModHeadFixed>
      {count > 0 ? (
        <>
          <div className={slots.products()}>{children}</div>
        </>
      ) : (
        <div className={slots.notFound({ isSearchPage })}>
          {isSearchPage ? (
            <div>
              <div className={slots.notFoundTitle()}>
                {t('searchPage.notFound.title')}
              </div>
              <div className={slots.notFoundDescription()}>
                {t('searchPage.notFound.productsDescription')}
              </div>
            </div>
          ) : (
            t('productCatalog.notFound')
          )}
        </div>
      )}
      <div className={slots.pagination()}>
        {count > 1 && (
          <UiKitPaginationBase
            count={count}
            page={page}
            onChange={handlePaginationChange}
            getPageHref={getPageHref}
            disabledNavigation
            onLoadMore={onLoadMore}
          />
        )}
      </div>
      {Boolean(bottomSeoBlock) && (
        <div className={slots.bottomSeoBlock()}>{bottomSeoBlock}</div>
      )}
    </div>
  );
}
